import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSyncStatusIndicator } from '../hooks/useSyncStatusIndicator';
import { Link } from 'react-router-dom';
import { DevicePhoneMobileIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { SyncIssuesDialog } from './SyncIssuesDialog';
import { IconButton, Spinner, cx, focusRing } from './ui';

const TONE_STYLES = {
  offline: { pill: 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-400', dot: 'bg-red-500 dark:bg-red-400' },
  syncing: { pill: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-500/15 dark:text-indigo-300', dot: 'bg-indigo-500 dark:bg-indigo-400' },
  error: { pill: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/30 dark:bg-orange-500/15 dark:text-orange-400', dot: 'bg-orange-500 dark:bg-orange-400' },
  pending: { pill: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-400', dot: 'bg-amber-500 dark:bg-amber-400' },
  synced: { pill: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-400', dot: 'bg-emerald-500 dark:bg-emerald-400' },
};

interface SyncStatusIndicatorProps {
  className?: string;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ className = '' }) => {
  const { isOnline, isSyncing, pendingActions, lastSyncTime, lastSyncError, failedActions, startSync, startForceFullResync, retryFailedActions, forgetFailedActions } = useSyncStatusIndicator();
  const { isLocal } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);

  const getStatusInfo = () => {
    if (!isOnline) {
      return {
        tone: 'offline' as const,
        text: 'Offline',
        clickable: false
      };
    }

    if (isSyncing) {
      return {
        tone: 'syncing' as const,
        text: 'Syncing...',
        clickable: false
      };
    }

    if (lastSyncError) {
      return {
        tone: 'error' as const,
        text: failedActions.length > 0 ? `${failedActions.length} failed` : 'Sync error',
        clickable: true,
        opensDialog: true,
      };
    }

    if (pendingActions.length > 0) {
      return {
        tone: 'pending' as const,
        text: `${pendingActions.length} pending`,
        clickable: true,
        opensDialog: false,
      };
    }

    return {
      tone: 'synced' as const,
      text: 'Synced',
      clickable: true,
      opensDialog: false,
    };
  };

  // Nothing syncs without an account, so there are no counts to show; the pill leads to sign-in.
  if (isLocal) {
    return (
      <Link
        to="/login"
        title="Sign in to sync"
        className={cx(
          'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-opacity hover:opacity-80',
          'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
          focusRing,
          className,
        )}
      >
        <DevicePhoneMobileIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        On this phone only
      </Link>
    );
  }

  const statusInfo = getStatusInfo();
  const toneStyle = TONE_STYLES[statusInfo.tone];

  const handleClick = () => {
    if (!statusInfo.clickable) return;
    if (statusInfo.opensDialog) {
      setDialogOpen(true);
    } else {
      startSync();
    }
  };

  const handleRetryAll = async () => {
    setDialogOpen(false);
    await retryFailedActions();
  };

  const handleDiscardAll = async () => {
    setDialogOpen(false);
    await forgetFailedActions();
  };

  const handleForceFullResync = async () => {
    await startForceFullResync();
    setDialogOpen(false);
  };

  const pill = (
    <>
      {statusInfo.tone === 'syncing'
        ? <Spinner className="h-3 w-3" />
        : <span className={cx('h-2 w-2 flex-none rounded-full', toneStyle.dot)} aria-hidden="true" />}
      <span>{statusInfo.text}</span>
      {lastSyncTime && (
        <span className="hidden text-xs font-normal opacity-70 tabular-nums dark:opacity-85 sm:inline">
          {lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </>
  );
  const pillClass = cx(
    'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium',
    toneStyle.pill,
    className,
  );

  return (
    <>
      <div className="inline-flex items-center gap-0.5">
        {statusInfo.clickable ? (
          <button
            type="button"
            onClick={handleClick}
            title={statusInfo.opensDialog ? 'See what failed' : 'Sync now'}
            className={cx(pillClass, 'transition-opacity hover:opacity-80', focusRing)}
          >
            {pill}
          </button>
        ) : (
          <span className={pillClass} role="status">{pill}</span>
        )}
        <IconButton
          label="Sync details"
          icon={EllipsisHorizontalIcon}
          size="sm"
          onClick={() => setDialogOpen(true)}
        />
      </div>

      <SyncIssuesDialog
        open={dialogOpen}
        items={failedActions}
        onRetryAll={handleRetryAll}
        onDiscardAll={handleDiscardAll}
        onClose={() => setDialogOpen(false)}
        onForceFullResync={handleForceFullResync}
        isSyncing={isSyncing}
      />
    </>
  );
};
